import React, { useState, useEffect } from 'react';
import { useUser, useAuth } from '@clerk/clerk-react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { track } from '../lib/ga';


// Mirrors the server's classifyMeetability (api/conversation/manager.ts): a
// relationship you were born into (parent, sibling, grandparent, child,
// aunt/uncle, cousin, niece/nephew, grandchild) is NOT one you "met", so the
// "how did you meet / how long have you known them" questions don't apply.
// Everything else (spouse, partner, friend, colleague, mentor) — and the
// empty/unknown default — is treated as "meetable".
function isMeetableRelationship(relationship: string): boolean {
  if (!relationship) return true;
  const padded = ' ' + relationship.toLowerCase().replace(/[^a-z\s-]/g, ' ').replace(/\s+/g, ' ') + ' ';
  const bornIntoWords = [
    'mother', 'mum', 'mom', 'mam', 'mama', 'ma', 'father', 'dad', 'daddy', 'pa', 'papa', 'parent',
    'sister', 'brother', 'sibling', 'grandmother', 'grandma', 'nan', 'nana', 'nanna', 'granny', 'gran',
    'grandfather', 'grandpa', 'grandad', 'granddad', 'pop', 'poppa', 'grandparent', 'son', 'daughter',
    'child', 'kid', 'aunt', 'auntie', 'aunty', 'uncle', 'cousin', 'niece', 'nephew', 'grandson',
    'granddaughter', 'grandchild', 'grandkid',
  ];
  return !bornIntoWords.some((w) => padded.includes(' ' + w + ' '));
}

// A "No"/blank answer to "did they have a nickname?" or "what was their
// preferred name?" is a negative response, not a real name — don't persist it as
// one, or the deceased could end up addressed as "No".
function isMeaningfulName(v: string): boolean {
  const t = (v || '').trim().toLowerCase();
  if (!t) return false;
  return ![
    'no', 'none', 'n/a', 'na', 'nil', 'nope', 'n', '-', '--', 'not applicable',
    'not relevant', 'no nickname', 'no preferred name', 'unknown', 'unsure', 'no idea',
  ].includes(t);
}

// One-tap suggestions for "what will you call them?" (the voice name used
// throughout the piece), seeded from the stated relationship: family get the
// relational terms surfaced, everyone else just gets the preferred name. Mirrors
// the role lists in the server's classifyMeetability / derivePronoun.
function suggestAddressTerms(relationship: string, preferredName: string, nickname: string): string[] {
  const out: string[] = [];
  const padded = ' ' + (relationship || '').toLowerCase().replace(/[^a-z\s-]/g, ' ').replace(/\s+/g, ' ') + ' ';
  const has = (words: string[]) => words.some((w) => padded.includes(' ' + w + ' '));
  if (has(['father', 'dad', 'daddy', 'pa', 'papa'])) out.push('Dad', 'Father', 'Pa');
  if (has(['mother', 'mum', 'mom', 'mam', 'mama', 'ma'])) out.push('Mum', 'Mother', 'Ma');
  if (has(['grandfather', 'grandpa', 'grandad', 'granddad', 'pop', 'poppa', 'pops'])) out.push('Grandad', 'Pop', 'Grandpa');
  if (has(['grandmother', 'grandma', 'nan', 'nana', 'nanna', 'granny', 'gran'])) out.push('Nan', 'Granny', 'Grandma');
  const pref = (preferredName || '').trim();
  if (pref) out.push(pref);
  const nick = (nickname || '').trim();
  if (isMeaningfulName(nick)) out.push(nick);
  // Dedupe case-insensitively, cap at 5.
  const seen = new Set<string>();
  const deduped: string[] = [];
  for (const t of out) {
    const k = t.toLowerCase();
    if (t && !seen.has(k)) { seen.add(k); deduped.push(t); }
  }
  return deduped.slice(0, 5);
}

const Onboarding: React.FC = () => {
  const { isLoaded, isSignedIn, user } = useUser();
  const { getToken } = useAuth();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedType, setSelectedType] = useState<'eulogy' | 'tribute' | null>(null);
  const [deceasedName, setDeceasedName] = useState('');
  const [preferredName, setPreferredName] = useState('');
  const [nickname, setNickname] = useState('');
  // 2026-06-28: the "voice name" — what the writer will call the deceased
  // throughout the piece (defaults to the preferred name; can be a relational
  // term like "Mum"/"Dad"/"Granny").
  const [addressName, setAddressName] = useState('');
  const [relationship, setRelationship] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [dateOfPassing, setDateOfPassing] = useState('');
  const [placeOfBirth, setPlaceOfBirth] = useState('');
  const [howMet, setHowMet] = useState('');
  const [howLongKnown, setHowLongKnown] = useState('');
  const [visitedSteps, setVisitedSteps] = useState<Set<number>>(new Set([1]));
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<'loading' | 'active' | 'inactive'>('loading');

  useEffect(() => {
    document.title = 'Cyrano - Eulogy Generator';
    // Scroll to top on page load
    window.scrollTo(0, 0);
  }, []);

  // Funnel analytics: which onboarding step people reach (and where they stop).
  useEffect(() => {
    track('onboarding_step', { step: currentStep });
  }, [currentStep]);

  // Default the voice name to the preferred name the first time a preferred name
  // is entered, so the field is never empty and reads as a deliberate choice.
  // The writer can then swap it for a relational term (Mum / Dad / Granny) via
  // the chips or by typing. Only fills when still blank, so later edits stick.
  useEffect(() => {
    if (!addressName.trim() && preferredName.trim()) {
      setAddressName(preferredName.trim());
    }
  }, [preferredName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check subscription status for returning users
  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      if (!isLoaded || !isSignedIn) return;

      try {
        const token = await getToken();
        if (!token) {
          setSubscriptionStatus('inactive');
          return;
        }

        const response = await fetch('/api/subscription/status', {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          console.log('Onboarding - Subscription status response:', data);
          // Only treat 'active' as active, everything else is inactive
          setSubscriptionStatus(data.subscription_status === 'active' ? 'active' : 'inactive');
        } else {
          console.error('Onboarding - Subscription status API error:', response.status, response.statusText);
          setSubscriptionStatus('inactive');
        }
      } catch (error) {
        console.error('Error checking subscription status:', error);
        setSubscriptionStatus('inactive');
      }
    };

    checkSubscriptionStatus();
  }, [isLoaded, isSignedIn, getToken]);

  // Show loading spinner while Clerk loads or checking subscription
  if (!isLoaded || subscriptionStatus === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-warm-background">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-space-cadet"></div>
      </div>
    );
  }

  // Redirect to sign in if not authenticated
  if (!isSignedIn) {
    return <Navigate to="/sign-in" replace />;
  }

  // Returning users with active subscription go straight to dashboard
  // unless they clicked "Create New" (which adds ?new=true)
  const isReturningUser = subscriptionStatus === 'active';
  const isCreatingNew = searchParams.get('new') === 'true';

  if (isReturningUser && !isCreatingNew) {
    return <Navigate to="/dashboard" replace />;
  }

  // All users get 3 steps — returning users skip step 3 only if they
  // already have a license for the person they entered (checked in handleNext)
  const totalSteps = 3;

  const handlePayment = async () => {
    if (!selectedType || !deceasedName.trim()) {
      alert('Please complete all required fields');
      return;
    }

    setIsProcessingPayment(true);

    try {
      // Get JWT token - ensure we get a JWT that can be verified by backend
      // getToken() without options should return a JWT by default
      const token = await getToken();
      if (!token) {
        console.error('❌ No token returned from getToken()');
        throw new Error('Authentication required. Please log in again.');
      }

      console.log('✅ Token retrieved:', token.substring(0, 20) + '...');
      console.log('✅ Token length:', token.length);
      
      // Basic JWT structure check (should have 3 parts separated by dots)
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) {
        console.error('❌ Token does not appear to be a valid JWT (expected 3 parts, got', tokenParts.length, ')');
        throw new Error('Invalid token format. Please log in again.');
      }

      const response = await fetch('/api/stripe/create-onetime-checkout', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: selectedType,
          name: deceasedName,
          relationship: relationship,
          // 2026-07-05: send the full onboarding answers so they are persisted
          // on the license at payment time (Stripe metadata → webhook →
          // testament_licenses.onboarding_context, migration 028). This makes
          // them durable — localStorage remains the primary copy, but nothing
          // is lost if storage is cleared or the user returns on another device.
          onboarding: {
            preferredName: isMeaningfulName(preferredName) ? preferredName.trim() : '',
            nickname: isMeaningfulName(nickname) ? nickname.trim() : '',
            addressName: isMeaningfulName(addressName)
              ? addressName.trim()
              : (isMeaningfulName(preferredName) ? preferredName.trim() : ''),
            dateOfBirth: dateOfBirth || '',
            dateOfPassing: dateOfPassing || '',
            placeOfBirth: placeOfBirth || '',
            howMet: isMeetableRelationship(relationship) ? (howMet || '') : '',
            howLongKnown: isMeetableRelationship(relationship) ? (howLongKnown || '') : '',
          },
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        console.error('❌ Payment API error:', {
          status: response.status,
          error: data.error,
          message: data.message,
        });
        throw new Error(data.error || data.message || 'Failed to create checkout session');
      }

      // Store relationship in localStorage for auto-advance after payment
      if (relationship) {
        localStorage.setItem('cyrano_relationship', relationship);
      }

      // Save all onboarding answers so the conversation doesn't re-ask them
      const userNameForSave = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || '';
      localStorage.setItem('cyrano_onboarding_data', JSON.stringify({
        type: selectedType,
        name: deceasedName,
        preferredName: isMeaningfulName(preferredName) ? preferredName : '',
        nickname: isMeaningfulName(nickname) ? nickname : '',
        // 2026-06-28: the voice name used throughout the piece. Falls back to the
        // preferred name when blank, so the conversation always has one.
        addressName: isMeaningfulName(addressName)
          ? addressName.trim()
          : (isMeaningfulName(preferredName) ? preferredName.trim() : ''),
        relationship: relationship,
        dateOfBirth: dateOfBirth || '',
        dateOfPassing: dateOfPassing || '',
        placeOfBirth: placeOfBirth || '',
        howMet: isMeetableRelationship(relationship) ? (howMet || '') : '',
        howLongKnown: isMeetableRelationship(relationship) ? (howLongKnown || '') : '',
        userName: userNameForSave,
      }));

      // Belt-and-braces: persist the Clerk user identity to localStorage so
      // conversation-interface.html can recover it if the redirect chain
      // (Onboarding → Stripe → PaymentSuccess → ConversationPage) ever fails
      // to propagate userId on the URL. Without this, the static page falls
      // into local-only fallback mode — no DB, template eulogy, lost answers.
      // Kept under separate keys (not inside cyrano_onboarding_data) because
      // that blob is name-gated and userId is independent of the deceased.
      if (user?.id) {
        localStorage.setItem('cyrano_user_id', user.id);
      }
      const userEmailForSave = user?.primaryEmailAddress?.emailAddress || '';
      if (userEmailForSave) {
        localStorage.setItem('cyrano_user_email', userEmailForSave);
      }
      if (userNameForSave) {
        localStorage.setItem('cyrano_user_name', userNameForSave);
      }

      // Redirect to Stripe hosted checkout (reverting to working solution)
      if (data.checkoutUrl) {
        track('begin_checkout', { type: selectedType || 'eulogy' });
        window.location.href = data.checkoutUrl;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error: any) {
      console.error('Payment error:', error);
      alert(error.message || 'Failed to process payment. Please try again.');
      setIsProcessingPayment(false);
    }
  };

  const handleNext = () => {
    if (currentStep === 1) {
      if (!selectedType) {
        alert('Please select either Eulogy or Tribute');
        return;
      }
      setVisitedSteps(prev => new Set([...prev, 2]));
      setCurrentStep(2);
    } else if (currentStep === 2) {
      if (!deceasedName.trim()) {
        alert('Please enter your loved one\'s name');
        return;
      }
      if (!preferredName.trim()) {
        alert('Please enter their preferred name (the name we\'ll use throughout)');
        return;
      }
      if (!relationship.trim()) {
        alert('Please enter your relationship to them');
        return;
      }
      // If arriving via ?new=true, always require payment for the new person.
      // Existing licensed people are handled by /new-project (no onboarding needed).
      setVisitedSteps(prev => new Set([...prev, 3]));
      setCurrentStep(3);
    } else {
      // Step 3 - trigger payment
      handlePayment();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (step: number) => {
    // Allow navigation to:
    // 1. Completed steps (steps before current)
    // 2. Current step (no-op, but allows visual feedback)
    // 3. Already visited steps (if they've been there before)
    if (step < currentStep || visitedSteps.has(step)) {
      setCurrentStep(step);
    }
    // Don't allow navigation to unvisited future steps
  };

  const progressPercentage = ((currentStep - 1) / (totalSteps - 1)) * 100;

  return (
    <div className="min-h-screen bg-warm-background px-4 py-8">
      <div className="max-w-[800px] mx-auto">
        {/* Logo Section with Home Link */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img 
              src="/Assets/GhostQuillLogo.png" 
              alt="Cyrano" 
              className="w-9 h-9 object-contain"
            />
            <span className="font-serif text-[2rem] font-semibold text-space-cadet">
              Cyrano
            </span>
          </div>
          <a 
            href="/landing.html"
            className="text-cool-gray text-sm hover:text-space-cadet-primary transition-colors inline-flex items-center gap-1"
          >
            ← Home
          </a>
        </div>

        {/* Progress Bar */}
        <div className="mb-12">
          <div className="flex justify-between items-center relative mb-4">
            {/* Progress Line Background */}
            <div className="absolute top-5 left-0 right-0 h-1 bg-cool-gray/20 z-0 rounded-full"></div>
            {/* Progress Line Fill - Using space-cadet-primary for better visibility */}
            <div 
              className="absolute top-5 left-0 h-1 bg-space-cadet-primary z-10 transition-all duration-300 rounded-full"
              style={{ width: `${progressPercentage}%` }}
            ></div>

            {/* Steps */}
            {[1, 2, 3].map((step) => {
              const isCompleted = step < currentStep;
              const isActive = step === currentStep;
              const isVisited = visitedSteps.has(step);
              const isClickable = isCompleted || isVisited || isActive;
              
              return (
                <div key={step} className="relative z-20 flex flex-col items-center flex-1">
                  <button
                    onClick={() => handleStepClick(step)}
                    disabled={!isClickable}
                    className={`w-12 h-12 rounded-full flex items-center justify-center font-semibold mb-2 transition-all ${
                      isCompleted
                        ? 'bg-space-cadet-primary border-2 border-space-cadet-primary text-white shadow-[0_2px_8px_rgba(25,40,92,0.3)] hover:bg-space-cadet-primary/90 hover:shadow-[0_4px_12px_rgba(25,40,92,0.4)] cursor-pointer'
                        : isActive
                        ? 'bg-space-cadet-primary border-2 border-space-cadet-primary text-white shadow-[0_0_0_4px_rgba(25,40,92,0.15)] cursor-pointer'
                        : isVisited
                        ? 'bg-pink-lavender border-2 border-pink-lavender text-white hover:bg-pink-lavender/90 cursor-pointer'
                        : 'bg-white border-2 border-cool-gray/30 text-cool-gray cursor-not-allowed'
                    }`}
                    title={
                      isCompleted 
                        ? `Go to ${step === 1 ? 'Account' : step === 2 ? 'Details' : 'Ready'}`
                        : isActive
                        ? `Current step: ${step === 1 ? 'Account' : step === 2 ? 'Details' : 'Ready'}`
                        : isVisited
                        ? `Return to ${step === 1 ? 'Account' : step === 2 ? 'Details' : 'Ready'}`
                        : 'Complete previous steps first'
                    }
                  >
                    {isCompleted || isVisited ? '✓' : step}
                  </button>
                  <div
                    className={`text-sm text-center font-medium ${
                      isActive || isCompleted 
                        ? 'text-space-cadet-primary' 
                        : isVisited
                        ? 'text-pink-lavender'
                        : 'text-cool-gray'
                    }`}
                  >
                    {step === 1 ? 'Account' : step === 2 ? 'Details' : 'Ready'}
                </div>
              </div>
              );
            })}
          </div>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl p-5 sm:p-8 md:p-12 shadow-[0_8px_32px_rgba(25,40,92,0.12)] border border-space-cadet/8">
          {/* Welcome Text - Only show on Step 1 */}
          {currentStep === 1 && (
            <div className="text-center mb-10">
              <h1 className="font-serif text-[2.25rem] font-semibold text-space-cadet mb-3">
                Let's Get Started
              </h1>
              <p className="text-cool-gray text-lg leading-relaxed">
                We'll guide you through creating a meaningful tribute or eulogy
              </p>
            </div>
          )}

          {/* Step 1: Eulogy or Tribute */}
          {currentStep === 1 && (
            <div>
              <h2 className="font-serif text-2xl font-semibold text-space-cadet mb-6">
                What would you like to create?
              </h2>
              
              {/* Instruction Box with Arrow */}
              <div className="bg-pink-lavender/10 border-l-4 border-pink-lavender rounded-lg p-5 mb-8 flex items-start gap-4">
                <img
                  src="/Assets/200-handdrawn-icons-by-heloholo/png/64/arrow_right64.png"
                  alt="Arrow"
                  className="w-6 h-6 flex-shrink-0 mt-0.5"
                />
                <p className="text-space-cadet font-medium text-base leading-relaxed">
                  Please select one option below to continue
                  </p>
                </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
                {/* Eulogy Card */}
                <label
                  className={`relative rounded-xl p-6 md:p-10 cursor-pointer transition-all duration-300 ${
                    selectedType === 'eulogy'
                      ? 'bg-space-cadet-primary/5 shadow-[0_8px_24px_rgba(25,40,92,0.2)]'
                      : 'border-2 border-cool-gray/20 hover:border-space-cadet-primary/50 hover:-translate-y-1 hover:shadow-[0_6px_20px_rgba(25,40,92,0.15)]'
                  }`}
                  style={{
                    borderWidth: selectedType === 'eulogy' ? '3px' : '2px',
                    borderColor: selectedType === 'eulogy' ? '#19285c' : undefined,
                  }}
                  onClick={() => setSelectedType('eulogy')}
                >
                  <input
                    type="radio"
                    name="type"
                    value="eulogy"
                    checked={selectedType === 'eulogy'}
                    onChange={() => setSelectedType('eulogy')}
                    className="sr-only"
                  />
                  
                  {/* Tick Icon - Top Right Corner */}
                  {selectedType === 'eulogy' && (
                    <div className="absolute top-4 right-4">
                      <img
                        src="/Assets/200-handdrawn-icons-by-heloholo/png/64/check_ok64.png"
                        alt="Selected"
                        className="w-8 h-8"
                      />
                    </div>
                  )}
                  
                  <img
                    src="/Assets/200-handdrawn-icons-by-heloholo/png/64/book64.png"
                    alt="Eulogy"
                    className="w-20 h-20 mb-6 block"
                  />
                  <h3 className="font-serif text-2xl font-semibold text-space-cadet mb-4">
                    A Eulogy
                  </h3>
                  <p className="text-cool-gray text-base mb-6 leading-relaxed">
                    An overview of a life, covering the journey from birth through to the present. It includes the highlights, milestones, relationships, and achievements that defined them.
                  </p>
                  <div className="bg-pink-lavender/10 border-l-4 border-pink-lavender p-5 rounded-md">
                    <div className="font-semibold text-space-cadet text-sm mb-2">Best for:</div>
                    <div className="text-cool-gray text-sm leading-relaxed">
                      Telling the complete story of their life. Cyrano will help you structure this journey in a way that feels complete and meaningful.
                    </div>
                  </div>
                </label>

                {/* Tribute Card */}
                <label
                  className={`relative rounded-xl p-6 md:p-10 cursor-pointer transition-all duration-300 ${
                    selectedType === 'tribute'
                      ? 'bg-space-cadet-primary/5 shadow-[0_8px_24px_rgba(25,40,92,0.2)]'
                      : 'border-2 border-cool-gray/20 hover:border-space-cadet-primary/50 hover:-translate-y-1 hover:shadow-[0_6px_20px_rgba(25,40,92,0.15)]'
                  }`}
                  style={{
                    borderWidth: selectedType === 'tribute' ? '3px' : '2px',
                    borderColor: selectedType === 'tribute' ? '#19285c' : undefined,
                  }}
                  onClick={() => setSelectedType('tribute')}
                >
                  <input
                    type="radio"
                    name="type"
                    value="tribute"
                    checked={selectedType === 'tribute'}
                    onChange={() => setSelectedType('tribute')}
                    className="sr-only"
                  />
                  
                  {/* Tick Icon - Top Right Corner */}
                  {selectedType === 'tribute' && (
                    <div className="absolute top-4 right-4">
                      <img
                        src="/Assets/200-handdrawn-icons-by-heloholo/png/64/check_ok64.png"
                        alt="Selected"
                        className="w-8 h-8"
                      />
                    </div>
                  )}
                  
                  <img
                    src="/Assets/200-handdrawn-icons-by-heloholo/png/64/love64.png"
                    alt="Tribute"
                    className="w-20 h-20 mb-6 block"
                  />
                  <h3 className="font-serif text-2xl font-semibold text-space-cadet mb-4">
                    A Tribute
                  </h3>
                  <p className="text-cool-gray text-base mb-6 leading-relaxed">
                    A more personal reflection focusing on your unique relationship with your loved one. It might be a story that captures their personality, a treasured memory, or an expression of what they meant to you.
                  </p>
                  <div className="bg-pink-lavender/10 border-l-4 border-pink-lavender p-5 rounded-md">
                    <div className="font-semibold text-space-cadet text-sm mb-2">Best for:</div>
                    <div className="text-cool-gray text-sm leading-relaxed">
                      Sharing your personal connection and the ways they touched your life. A tribute is yours and yours alone.
                    </div>
                  </div>
                </label>
              </div>

              {/* Selection Feedback */}
              {selectedType && (
                <div className="mt-6 p-5 bg-pink-lavender/10 border-l-4 border-pink-lavender rounded-lg">
                  <p className="text-space-cadet font-medium">
                    ✓ You've selected: <span className="font-semibold">{selectedType === 'eulogy' ? 'A Eulogy' : 'A Tribute'}</span>
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Details */}
          {currentStep === 2 && (
            <div>
              {/* Header with Icon */}
              <div className="text-center mb-8">
                <img
                  src={selectedType === 'eulogy' 
                    ? "/Assets/200-handdrawn-icons-by-heloholo/png/64/book64.png"
                    : "/Assets/200-handdrawn-icons-by-heloholo/png/64/love64.png"
                  }
                  alt={selectedType === 'eulogy' ? 'Eulogy' : 'Tribute'}
                  className="w-16 h-16 mx-auto mb-4"
                />
                <h2 className="font-serif text-2xl font-semibold text-space-cadet mb-3">
                  Tell us about your loved one
                </h2>
                <p className="text-cool-gray text-base leading-relaxed">
                  This helps us personalize your experience and create a meaningful {selectedType === 'eulogy' ? 'eulogy' : 'tribute'}
                </p>
              </div>

              {/* Common Fields for Both Types */}
              {/* Full Name */}
              <div className="mb-6">
                <label className="block font-medium text-space-cadet text-[0.95rem] mb-2">
                  What was their full name?<span className="text-red-600 ml-1">*</span>
                </label>
                <input
                  type="text"
                  value={deceasedName}
                  onChange={(e) => setDeceasedName(e.target.value)}
                  placeholder="Enter their full name"
                  className="w-full py-3.5 px-4 rounded-lg text-base text-elegant-black focus:ring-0 focus:ring-offset-0 focus:shadow-[0_0_0_3px_rgba(25,40,92,0.1)]"
                  style={{
                    borderWidth: '2px',
                    borderStyle: 'solid',
                    borderColor: '#8c83b4'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#d4adbb';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#8c83b4';
                  }}
                  required
                />
              </div>

              {/* Preferred Name */}
              <div className="mb-6">
                <label className="block font-medium text-space-cadet text-[0.95rem] mb-2">
                  What was their preferred name?<span className="text-red-600 ml-1">*</span>
                </label>
                <input
                  type="text"
                  value={preferredName}
                  onChange={(e) => setPreferredName(e.target.value)}
                  placeholder="The name they were known by day to day"
                  className="w-full py-3.5 px-4 rounded-lg text-base text-elegant-black focus:ring-0 focus:ring-offset-0 focus:shadow-[0_0_0_3px_rgba(25,40,92,0.1)] placeholder:text-cool-gray/50"
                  style={{
                    borderWidth: '2px',
                    borderStyle: 'solid',
                    borderColor: '#8c83b4'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#d4adbb';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#8c83b4';
                  }}
                  required
                />
                <p className="text-cool-gray text-sm mt-2 italic">
                  This is the name we'll use throughout. It may differ from their full name (for example, a "Margaret" who everyone called "Maggie"), and it's different from a nickname.
                </p>
              </div>

              {/* Nickname */}
              <div className="mb-6">
                <label className="block font-medium text-space-cadet text-[0.95rem] mb-2">
                  Did they have a nickname?
                </label>
                <input
                  type="text"
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  placeholder="An affectionate or playful name, if any"
                  className="w-full py-3.5 px-4 rounded-lg text-base text-elegant-black focus:ring-0 focus:ring-offset-0 focus:shadow-[0_0_0_3px_rgba(25,40,92,0.1)] placeholder:text-cool-gray/50"
                  style={{
                    borderWidth: '2px',
                    borderStyle: 'solid',
                    borderColor: '#8c83b4'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#d4adbb';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#8c83b4';
                  }}
                />
                <p className="text-cool-gray text-sm mt-2 italic">
                  Leave this blank if they didn't have one.
                </p>
              </div>

              {/* Relationship */}
              <div className="mb-6">
                <label className="block font-medium text-space-cadet text-[0.95rem] mb-2">
                  What was your relationship to them?<span className="text-red-600 ml-1">*</span>
                </label>
                <input
                  type="text"
                  value={relationship}
                  onChange={(e) => setRelationship(e.target.value)}
                  placeholder="e.g., she was my mother, my best friend since high-school, he was my husband, my work mentor and colleague"
                  className="w-full py-3.5 px-4 rounded-lg text-base text-elegant-black focus:ring-0 focus:ring-offset-0 focus:shadow-[0_0_0_3px_rgba(25,40,92,0.1)] placeholder:text-cool-gray/50"
                  style={{
                    borderWidth: '2px',
                    borderStyle: 'solid',
                    borderColor: '#8c83b4'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#d4adbb';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#8c83b4';
                  }}
                  required
                />
                <p className="text-cool-gray text-sm mt-2 italic">
                  Be as specific as you like, every relationship is unique. This helps us ask the right questions for your perspective
                </p>
              </div>

              {/* Voice name - what the writer calls them throughout the piece */}
              <div className="mb-6">
                <label className="block font-medium text-space-cadet text-[0.95rem] mb-2">
                  What will you call them in the {selectedType === 'tribute' ? 'tribute' : 'eulogy'}?<span className="text-red-600 ml-1">*</span>
                </label>
                {suggestAddressTerms(relationship, preferredName, nickname).length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {suggestAddressTerms(relationship, preferredName, nickname).map((term) => {
                      const selected = addressName.trim().toLowerCase() === term.toLowerCase();
                      return (
                        <button
                          key={term}
                          type="button"
                          onClick={() => setAddressName(term)}
                          className="px-3 py-1.5 rounded-full text-sm transition-colors"
                          style={{
                            borderWidth: '2px',
                            borderStyle: 'solid',
                            borderColor: selected ? '#19285c' : '#8c83b4',
                            background: selected ? '#19285c' : 'transparent',
                            color: selected ? '#ffffff' : '#19285c',
                          }}
                        >
                          {term}
                        </button>
                      );
                    })}
                  </div>
                )}
                <input
                  type="text"
                  value={addressName}
                  onChange={(e) => setAddressName(e.target.value)}
                  placeholder={preferredName ? `For example, ${preferredName}` : 'For example, Mum, Dad, Granny, or their name'}
                  className="w-full py-3.5 px-4 rounded-lg text-base text-elegant-black focus:ring-0 focus:ring-offset-0 focus:shadow-[0_0_0_3px_rgba(25,40,92,0.1)] placeholder:text-cool-gray/50"
                  style={{
                    borderWidth: '2px',
                    borderStyle: 'solid',
                    borderColor: '#8c83b4'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#d4adbb';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#8c83b4';
                  }}
                />
                <p className="text-cool-gray text-sm mt-2 italic">
                  This is how you'll refer to them in your own words throughout. It might be their preferred name, or something personal like "Mum", "Dad" or "Granny". {preferredName ? `If you leave it blank, we'll use "${preferredName}".` : "If you leave it blank, we'll use their preferred name."}{isMeaningfulName(nickname) ? ` Their nickname "${nickname}" will still be woven in where it fits, even if it isn't what you call them.` : ''}
                </p>
              </div>

              {/* Eulogy-Specific Fields */}
              {selectedType === 'eulogy' && (
                <>
                  {/* Date of Birth */}
                  <div className="mb-6">
                    <label className="block font-medium text-space-cadet text-[0.95rem] mb-2">
                      What is their Date of Birth?
                    </label>
                    <input
                      type="date"
                      value={dateOfBirth}
                      onChange={(e) => setDateOfBirth(e.target.value)}
                      className="w-full py-3.5 px-4 rounded-lg text-base text-elegant-black focus:ring-0 focus:ring-offset-0 focus:shadow-[0_0_0_3px_rgba(25,40,92,0.1)]"
                      style={{
                        borderWidth: '2px',
                        borderStyle: 'solid',
                        borderColor: '#8c83b4'
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#d4adbb';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = '#8c83b4';
                      }}
                    />
                    <p className="text-cool-gray text-sm mt-2 italic">
                      Format: {(() => {
                        // Detect date format based on locale
                        const testDate = new Date(2023, 0, 15); // Jan 15, 2023
                        const formatted = new Intl.DateTimeFormat(navigator.language || 'en-AU').formatToParts(testDate);
                        const order = formatted.map(p => p.type).filter(t => ['day', 'month', 'year'].includes(t));
                        
                        // US format is month/day/year
                        // Most other formats are day/month/year
                        if (order[0] === 'month') {
                          return 'mm/dd/yyyy';
                        }
                        return 'dd/mm/yyyy';
                      })()}
                    </p>
                  </div>

                  {/* Date of Passing */}
                  <div className="mb-6">
                    <label className="block font-medium text-space-cadet text-[0.95rem] mb-2">
                      What is their Date of Passing?
                    </label>
                    <input
                      type="date"
                      value={dateOfPassing}
                      onChange={(e) => setDateOfPassing(e.target.value)}
                      className="w-full py-3.5 px-4 rounded-lg text-base text-elegant-black focus:ring-0 focus:ring-offset-0 focus:shadow-[0_0_0_3px_rgba(25,40,92,0.1)]"
                      style={{
                        borderWidth: '2px',
                        borderStyle: 'solid',
                        borderColor: '#8c83b4'
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#d4adbb';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = '#8c83b4';
                      }}
                    />
                    <p className="text-cool-gray text-sm mt-2 italic">
                      This appears with their date of birth on your exported documents. You can leave it blank for now and add it later.
                    </p>
                  </div>

                  {/* Place of Birth */}
                  <div className="mb-6">
                    <label className="block font-medium text-space-cadet text-[0.95rem] mb-2">
                      Where were they born?
                    </label>
                    <input
                      type="text"
                      value={placeOfBirth}
                      onChange={(e) => setPlaceOfBirth(e.target.value)}
                      placeholder="Enter city, state, or country"
                      className="w-full py-3.5 px-4 rounded-lg text-base text-elegant-black focus:ring-0 focus:ring-offset-0 focus:shadow-[0_0_0_3px_rgba(25,40,92,0.1)]"
                      style={{
                        borderWidth: '2px',
                        borderStyle: 'solid',
                        borderColor: '#8c83b4'
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#d4adbb';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = '#8c83b4';
                      }}
                    />
                  </div>
                </>
              )}

              {/* Tribute-Specific Fields — only for "meetable" relationships
                  (spouse, partner, friend, colleague). Born-into family (a
                  parent, sibling, grandparent, etc.) don't get "how did you meet
                  / how long known", since you don't "meet" family. */}
              {selectedType === 'tribute' && isMeetableRelationship(relationship) && (
                <>
                  {/* How Met */}
                  <div className="mb-6">
                    <label className="block font-medium text-space-cadet text-[0.95rem] mb-2">
                      Where and how did you meet them?
                    </label>
                    <input
                      type="text"
                      value={howMet}
                      onChange={(e) => setHowMet(e.target.value)}
                      placeholder="Tell us about how you met"
                      className="w-full py-3.5 px-4 rounded-lg text-base text-elegant-black focus:ring-0 focus:ring-offset-0 focus:shadow-[0_0_0_3px_rgba(25,40,92,0.1)]"
                      style={{
                        borderWidth: '2px',
                        borderStyle: 'solid',
                        borderColor: '#8c83b4'
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#d4adbb';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = '#8c83b4';
                      }}
                    />
                  </div>

                  {/* How Long Known */}
                  <div className="mb-6">
                    <label className="block font-medium text-space-cadet text-[0.95rem] mb-2">
                      How long have you known them for?
                    </label>
                    <input
                      type="text"
                      value={howLongKnown}
                      onChange={(e) => setHowLongKnown(e.target.value)}
                      placeholder="e.g., 10 years, Since childhood, etc."
                      className="w-full py-3.5 px-4 rounded-lg text-base text-elegant-black focus:ring-0 focus:ring-offset-0 focus:shadow-[0_0_0_3px_rgba(25,40,92,0.1)]"
                      style={{
                        borderWidth: '2px',
                        borderStyle: 'solid',
                        borderColor: '#8c83b4'
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#d4adbb';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = '#8c83b4';
                      }}
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 3: Ready - Paywall Gateway */}
          {currentStep === 3 && (
            <div className="text-center">
              <img
                src="/Assets/200-handdrawn-icons-by-heloholo/png/64/unlock64.png"
                alt="Unlock"
                className="w-16 h-16 mx-auto mb-6"
              />
              <h2 className="font-serif text-[2.25rem] font-semibold text-space-cadet mb-4">
                You've begun creating a meaningful {selectedType === 'eulogy' ? 'eulogy' : 'tribute'} for {deceasedName || 'your loved one'}
              </h2>
              <p className="text-space-cadet text-lg font-medium mb-2">
                Continue with full access to complete your {selectedType === 'eulogy' ? 'eulogy' : 'tribute'}
              </p>
              <p className="text-space-cadet text-lg font-semibold mb-8">
                $29.95 AUD • Full access • No subscription • Export and keep your tribute forever
              </p>
              <p className="text-cool-gray text-sm mb-8 italic">
                Have a guest pass code? Enter it at checkout
              </p>

              {/* Features List */}
              <div className="text-left max-w-2xl mx-auto mb-10">
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <span className="text-space-cadet-primary mt-1">✓</span>
                    <span className="text-cool-gray text-base leading-relaxed">
                      Thoughtfully guided questions crafted by professional celebrants
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-space-cadet-primary mt-1">✓</span>
                    <span className="text-cool-gray text-base leading-relaxed">
                      Share memories by speaking or typing—whatever feels right
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-space-cadet-primary mt-1">✓</span>
                    <span className="text-cool-gray text-base leading-relaxed">
                      Full editing and revision tools for every word
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-space-cadet-primary mt-1">✓</span>
                    <span className="text-cool-gray text-base leading-relaxed">
                      Collaborate with up to 4 family or friends
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-space-cadet-primary mt-1">✓</span>
                    <span className="text-cool-gray text-base leading-relaxed">
                      Add photos from everyone—all contributors can upload images to include with your {selectedType === 'eulogy' ? 'eulogy' : 'tribute'}
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-space-cadet-primary mt-1">✓</span>
                    <span className="text-cool-gray text-base leading-relaxed">
                      Export to PDF, Word, PowerPoint, Google Docs, Socials and more
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-space-cadet-primary mt-1">✓</span>
                    <span className="text-cool-gray text-base leading-relaxed">
                      Your progress is automatically saved—resume anytime
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-space-cadet-primary mt-1">✓</span>
                    <span className="text-cool-gray text-base leading-relaxed">
                      Full access with no subscription fees
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex gap-4 mt-8">
            {currentStep > 1 && (
            <button
                onClick={handlePrevious}
                className="flex-1 transition-all hover:-translate-y-0.5"
                style={{
                  background: 'transparent',
                  color: '#19285c',
                  border: '2px solid #19285c',
                  padding: '1rem 2rem',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '500',
                  fontFamily: 'Inter, sans-serif'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#19285c';
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(25, 40, 92, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#19285c';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                ← Previous
            </button>
            )}
              <button
              onClick={handleNext}
              disabled={
                (currentStep === 1 && !selectedType) ||
                (currentStep === 2 && (!deceasedName.trim() || !preferredName.trim() || !relationship.trim())) ||
                isProcessingPayment
              }
              className={`flex-1 transition-all ${
                (currentStep === 1 && !selectedType) ||
                (currentStep === 2 && (!deceasedName.trim() || !preferredName.trim() || !relationship.trim())) ||
                isProcessingPayment
                  ? 'cursor-not-allowed'
                  : 'hover:-translate-y-0.5'
              }`}
              style={{
                background: (currentStep === 1 && !selectedType) || (currentStep === 2 && (!deceasedName.trim() || !preferredName.trim() || !relationship.trim())) || isProcessingPayment
                  ? 'transparent'
                  : '#131e40',
                color: (currentStep === 1 && !selectedType) || (currentStep === 2 && (!deceasedName.trim() || !preferredName.trim() || !relationship.trim())) || isProcessingPayment
                  ? '#8c83b4'
                  : 'white',
                border: '2px solid #131e40',
                padding: '1rem 2rem',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '500',
                fontFamily: 'Inter, sans-serif',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                if (!e.currentTarget.disabled) {
                  e.currentTarget.style.background = '#131e40';
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.borderColor = '#131e40';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(25, 40, 92, 0.2)';
                }
              }}
              onMouseLeave={(e) => {
                const isDisabled = (currentStep === 1 && !selectedType) || (currentStep === 2 && (!deceasedName.trim() || !preferredName.trim() || !relationship.trim())) || isProcessingPayment;
                if (isDisabled) {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#8c83b4';
                  e.currentTarget.style.borderColor = '#131e40';
                } else {
                  e.currentTarget.style.background = '#131e40';
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.borderColor = '#131e40';
                }
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {isProcessingPayment
                ? 'Processing...'
                : (isReturningUser && currentStep === 2)
                  ? 'Start Conversation →'
                  : currentStep === totalSteps
                  ? 'Continue & Pay $29.95 AUD →'
                  : 'Continue →'}
              </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
